import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Keyboard } from "@capacitor/keyboard";
import { TypewriterText, AileSahnesi } from "./AuthAnimations";
import { useLanguage } from "../../i18n/LanguageContext";
import LanguageSwitcher from "../LanguageSwitcher";
import { listOptions } from "../../utils/i18nOptions";
import LegalDocumentPage from "../../pages/legal/LegalDocumentPage";
import { areAndroidHealthFeaturesDisabled, isIOSPlatform } from "../../utils/platformFeatures";

function AuthGlyph({ name }) {
  const paths = {
    user: <><circle cx="12" cy="8" r="3.25" /><path d="M5.5 19c.45-3.45 2.62-5.25 6.5-5.25s6.05 1.8 6.5 5.25" /></>,
    lock: <><rect x="5.5" y="10" width="13" height="10" rx="3" /><path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10" /><path d="M12 14.25v2.5" /></>,
    eye: <><path d="M2.75 12s3.35-5.25 9.25-5.25S21.25 12 21.25 12 17.9 17.25 12 17.25 2.75 12 2.75 12Z" /><circle cx="12" cy="12" r="2.4" /></>,
    eyeOff: <><path d="M4.1 4.1 19.9 19.9" /><path d="M9.65 7.08A9.9 9.9 0 0 1 12 6.75c5.9 0 9.25 5.25 9.25 5.25a15.8 15.8 0 0 1-2.23 2.75" /><path d="M14.5 16.9a9.7 9.7 0 0 1-2.5.35C6.1 17.25 2.75 12 2.75 12a15.5 15.5 0 0 1 2.4-2.9" /><path d="M10.3 10.3a2.4 2.4 0 0 0 3.4 3.4" /></>,
    sun: <><circle cx="12" cy="12" r="3.6" /><path d="M12 2.4v2M12 19.6v2M2.4 12h2M19.6 12h2M5.2 5.2l1.42 1.42M17.38 17.38l1.42 1.42M18.8 5.2l-1.42 1.42M6.62 17.38 5.2 18.8" /></>,
    moon: <path d="M20.2 15.2A8.25 8.25 0 0 1 8.8 3.8 8.25 8.25 0 1 0 20.2 15.2Z" />,
  };

  return (
    <svg className="authGlyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round">
        {paths[name]}
      </g>
    </svg>
  );
}

export default function AuthPage({
  /* giriş */
  loginBilgi, setLoginBilgi, sifre, setSifre, kayitSifre, setKayitSifre, sifreInputRef,
  sifreGoster, setSifreGoster,
  hata, bilgiMesaji, yukleniyor,
  girisYap, sosyalGirisYap, sifremiUnuttum,
  /* mail doğrulama */
  emailDogrulamaBekliyor = false,
  userEmail,
  dogrulamaMailiGonderildi,
  dogrulamaMailiTekrarGonder,
  dogrulamaEmailDegistir,
  emailDogrulamaKontrolEt,
  cikisYap,
  /* kayıt */
  kayitModu, setKayitModu,
  yasalOnay, setYasalOnay,
  ad, setAd, soyad, setSoyad,
  dogumTarihi, setDogumTarihi, cinsiyet, setCinsiyet,
  telefon, setTelefon, kullaniciAdi, setKullaniciAdi,
  rol, setRol, kanGrubu, setKanGrubu,
  boy, setBoy, kilo, setKilo,
  email, setEmail,
  kayitOl,
  bekleyenSosyalKayit,
  kayitAlanHatasi,
  setKayitAlanHatasi,
  sosyalAkisiIptalEt,
  setHata,
  setBilgiMesaji,
  tema,
  setTema,
}) {
  const { t, tList } = useLanguage();
  const [sifreSifirlamaModu, setSifreSifirlamaModu] = useState(false);
  const [authLegalDocument, setAuthLegalDocument] = useState(null);
  const [verificationCooldown, setVerificationCooldown] = useState(0);
  const [verificationEmailEditOpen, setVerificationEmailEditOpen] = useState(false);
  const [verificationNextEmail, setVerificationNextEmail] = useState("");
  const [verificationPassword, setVerificationPassword] = useState("");
  const [verificationKeyboardHeight, setVerificationKeyboardHeight] = useState(0);
  const [saglikBilgileriAcik, setSaglikBilgileriAcik] = useState(false);
  const [yasalOnayHatasi, setYasalOnayHatasi] = useState(false);
  const [authViewportHeight, setAuthViewportHeight] = useState(() => {
    if (typeof window === "undefined") return 800;
    return window.visualViewport?.height || window.innerHeight;
  });
  const authPageRef = useRef(null);
  const loginInputRef = useRef(null);
  const authBaselineHeightRef = useRef(
    typeof window === "undefined" ? 800 : Math.max(window.innerHeight || 0, window.visualViewport?.height || 0)
  );
  const yasalOnayRef = useRef(null);
  const registerScrollRef = useRef(null);
  const nameFieldRef = useRef(null);
  const surnameFieldRef = useRef(null);
  const birthDateFieldRef = useRef(null);
  const usernameFieldRef = useRef(null);
  const emailFieldRef = useRef(null);
  const passwordFieldRef = useRef(null);
  const phoneFieldRef = useRef(null);
  const verificationPasswordRef = useRef(null);
  const verificationKeyboardHeightRef = useRef(0);
  const legalLayerRef = useRef(null);
  const legalTouchRef = useRef({ active: false, startX: 0, startY: 0, dx: 0 });
  const verificationCooldownKeyRef = useRef("");

  useEffect(() => {
    if (!emailDogrulamaBekliyor || dogrulamaMailiGonderildi !== true) return;
    const key = String(userEmail || "").trim().toLowerCase();
    if (!key || verificationCooldownKeyRef.current === key) return;
    verificationCooldownKeyRef.current = key;
    setVerificationCooldown(60);
  }, [dogrulamaMailiGonderildi, emailDogrulamaBekliyor, userEmail]);

  useEffect(() => {
    if (verificationCooldown <= 0) return undefined;
    const timer = window.setTimeout(() => setVerificationCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [verificationCooldown]);

  const resendVerification = async () => {
    const sent = await dogrulamaMailiTekrarGonder?.();
    if (sent) setVerificationCooldown(60);
  };

  const updateVerificationEmail = async (event) => {
    event.preventDefault();
    const result = await dogrulamaEmailDegistir?.(verificationNextEmail, verificationPassword);
    if (!result?.ok) return;
    setVerificationEmailEditOpen(false);
    setVerificationNextEmail("");
    setVerificationPassword("");
    verificationCooldownKeyRef.current = String(result.email || "").trim().toLowerCase();
    setVerificationCooldown(result.sent ? 60 : 0);
  };

  const verificationPasswordAlaniniGorunurTut = useCallback((keyboardHeight) => {
    const kaydir = () => {
      const container = authPageRef.current;
      const target = verificationPasswordRef.current;
      if (!container || !target || document.activeElement !== target) return;

      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const reportedViewportHeight = viewport?.height || window.innerHeight || 0;
      const layoutHeight = window.innerHeight || authBaselineHeightRef.current;
      const viewportAlreadyResized = reportedViewportHeight < layoutHeight - 80;
      const visibleHeight = Math.max(
        260,
        reportedViewportHeight - (viewportAlreadyResized ? 0 : Math.max(0, keyboardHeight || 0))
      );
      const targetRect = target.getBoundingClientRect();
      const desiredTop = viewportTop + Math.min(170, Math.max(86, visibleHeight * 0.34));
      const nextTop = container.scrollTop + targetRect.top - desiredTop;
      container.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
    };

    [40, 160, 320].forEach((delay) => window.setTimeout(kaydir, delay));
  }, []);

  useEffect(() => {
    if (!emailDogrulamaBekliyor || !verificationEmailEditOpen) return undefined;
    const viewport = window.visualViewport;
    let disposed = false;
    let nativeHandles = [];

    const applyKeyboardHeight = (height) => {
      const nextHeight = Math.max(0, Math.round(Number(height) || 0));
      verificationKeyboardHeightRef.current = nextHeight;
      setVerificationKeyboardHeight(nextHeight > 80 ? nextHeight : 0);
      if (nextHeight > 80) verificationPasswordAlaniniGorunurTut(nextHeight);
    };

    const updateFromViewport = () => {
      const baseline = Math.max(authBaselineHeightRef.current, window.innerHeight || 0);
      const measuredHeight = Math.max(
        0,
        Math.round(baseline - (viewport?.height || window.innerHeight || baseline) - (viewport?.offsetTop || 0))
      );
      const nextHeight = Math.max(verificationKeyboardHeightRef.current, measuredHeight);
      if (nextHeight > 80) applyKeyboardHeight(nextHeight);
    };

    const closeKeyboard = () => {
      verificationKeyboardHeightRef.current = 0;
      setVerificationKeyboardHeight(0);
    };

    const installNativeListeners = async () => {
      const showHandle = await Keyboard.addListener("keyboardWillShow", (info) => {
        applyKeyboardHeight(info?.keyboardHeight);
      });
      const hideHandle = await Keyboard.addListener("keyboardWillHide", closeKeyboard);
      if (disposed) {
        showHandle.remove();
        hideHandle.remove();
        return;
      }
      nativeHandles = [showHandle, hideHandle];
    };

    viewport?.addEventListener("resize", updateFromViewport);
    viewport?.addEventListener("scroll", updateFromViewport);
    installNativeListeners().catch(() => {});

    return () => {
      disposed = true;
      closeKeyboard();
      nativeHandles.forEach((handle) => handle.remove());
      viewport?.removeEventListener("resize", updateFromViewport);
      viewport?.removeEventListener("scroll", updateFromViewport);
    };
  }, [emailDogrulamaBekliyor, verificationEmailEditOpen, verificationPasswordAlaniniGorunurTut]);

  const openAuthLegal = useCallback((documentKey) => {
    if (!documentKey) return;
    setAuthLegalDocument(documentKey);
  }, []);

  const closeAuthLegal = useCallback(() => {
    const layer = legalLayerRef.current;
    if (!layer) { setAuthLegalDocument(null); return; }
    layer.classList.remove("authLegalLayerOpening");
    layer.classList.add("authLegalLayerClosing");
    layer.style.transition = "transform 230ms cubic-bezier(0.25, 0.46, 0.45, 0.94)";
    layer.style.transform = `translateX(${window.innerWidth}px)`;
    window.setTimeout(() => {
      setAuthLegalDocument(null);
    }, 240);
  }, []);

  useEffect(() => {
    if (!authLegalDocument) return undefined;

    // Yasal belge katmanı artık document.body altında portal olarak açılır.
    // Böylece auth ekranının flex/overflow düzenine dahil olup giriş-kayıt
    // sayfasının devamı gibi uzamaz; alttaki auth ekranı swipe sırasında hazır kalır.
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    const onPopState = () => closeAuthLegal();
    window.history.pushState({ authLegal: authLegalDocument }, "", window.location.href);
    window.addEventListener("popstate", onPopState, { once: true });

    return () => {
      window.removeEventListener("popstate", onPopState);
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [authLegalDocument, closeAuthLegal]);

  useEffect(() => {
    const layer = legalLayerRef.current;
    if (!layer || !authLegalDocument) return undefined;
    const touch = legalTouchRef.current;
    const onStart = (event) => {
      const point = event.touches?.[0];
      if (!point || point.clientX > 28 || event.target?.closest?.("[data-no-swipe-back='true']")) return;
      touch.active = true; touch.startX = point.clientX; touch.startY = point.clientY; touch.dx = 0;
      layer.style.transition = "none";
    };
    const onMove = (event) => {
      if (!touch.active) return;
      const point = event.touches?.[0]; if (!point) return;
      const dx = Math.max(0, point.clientX - touch.startX);
      const dy = Math.abs(point.clientY - touch.startY);
      if (dy > dx && dx < 12) { touch.active = false; return; }
      touch.dx = dx;
      layer.style.transform = `translateX(${dx}px)`;
      event.preventDefault();
    };
    const onEnd = () => {
      if (!touch.active) return;
      touch.active = false;
      if (touch.dx > Math.min(90, window.innerWidth * 0.22)) {
        closeAuthLegal();
      } else {
        layer.style.transition = "transform 190ms cubic-bezier(0.25, 0.46, 0.45, 0.94)";
        layer.style.transform = "translateX(0)";
        window.setTimeout(() => { layer.style.transition = ""; layer.style.transform = ""; }, 200);
      }
    };
    layer.addEventListener("touchstart", onStart, { passive: true });
    layer.addEventListener("touchmove", onMove, { passive: false });
    layer.addEventListener("touchend", onEnd, { passive: true });
    layer.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      layer.removeEventListener("touchstart", onStart);
      layer.removeEventListener("touchmove", onMove);
      layer.removeEventListener("touchend", onEnd);
      layer.removeEventListener("touchcancel", onEnd);
    };
  }, [authLegalDocument, closeAuthLegal]);

  const roller = listOptions(tList, "const.roles");
  const cinsiyetler = listOptions(tList, "const.genders");

  const mesajlariTemizle = () => {
    setHata?.("");
    setBilgiMesaji?.("");
    setKayitAlanHatasi?.("");
  };

  const hataAlani = (code) => ({
    name_required: "name", name_min: "name",
    surname_required: "surname", surname_min: "surname",
    birthDate_required: "birthDate", username_required: "username",
    email_invalid: "email", password_invalid: "password",
  }[code] || code);
  const alandaHataVar = (field) => hataAlani(kayitAlanHatasi) === field;
  const alaniTemizle = (field) => {
    if (alandaHataVar(field)) setKayitAlanHatasi?.("");
  };
  const kayitAlanHataMetni = (code) => {
    if (code === "name_required") return t("auth.error.nameRequired");
    if (code === "name_min") return t("auth.error.nameMin");
    if (code === "surname_required") return t("auth.error.surnameRequired");
    if (code === "surname_min") return t("auth.error.surnameMin");
    if (code === "birthDate_required") return t("auth.error.birthDateRequired");
    if (code === "username_required") return t("auth.error.usernameRequired");
    if (code === "email_invalid") return t("auth.error.emailInvalid");
    if (code === "password_invalid") return t("auth.error.passwordMin");
    if (code === "username") return t("auth.error.usernameTaken");
    if (code === "email") return t("auth.error.emailTaken");
    if (code === "phone") return t("auth.error.phoneTaken");
    return "";
  };

  const sifirlamaAlaniniGorunurTut = useCallback(() => {
    if (!sifreSifirlamaModu) return;
    const kaydir = () => {
      const container = authPageRef.current;
      const target = loginInputRef.current;
      if (!container || !target) return;
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const desiredTop = container.scrollTop + (targetRect.top - containerRect.top)
        - Math.max(20, (container.clientHeight - targetRect.height) * 0.42);
      container.scrollTo({ top: Math.max(0, desiredTop), behavior: "smooth" });
    };
    [60, 180, 360].forEach((delay) => window.setTimeout(kaydir, delay));
  }, [sifreSifirlamaModu]);

  useEffect(() => {
    if (!sifreSifirlamaModu) return undefined;
    const viewport = window.visualViewport;
    let nativeKeyboardHeight = 0;
    let disposed = false;
    let nativeHandles = [];

    const updateViewport = () => {
      if (nativeKeyboardHeight <= 0) {
        authBaselineHeightRef.current = Math.max(
          authBaselineHeightRef.current,
          window.innerHeight || 0,
          viewport?.height || 0
        );
      }
      const nextHeight = nativeKeyboardHeight > 0
        ? authBaselineHeightRef.current - nativeKeyboardHeight
        : (viewport?.height || window.innerHeight || authBaselineHeightRef.current);
      setAuthViewportHeight(Math.max(260, Math.round(nextHeight)));
      sifirlamaAlaniniGorunurTut();
    };

    const keyboardClosed = () => {
      nativeKeyboardHeight = 0;
      updateViewport();
    };

    const installNativeListeners = async () => {
      if (!isIOSPlatform()) return;
      const showHandle = await Keyboard.addListener("keyboardWillShow", (info) => {
        nativeKeyboardHeight = Math.max(0, Math.round(Number(info?.keyboardHeight) || 0));
        updateViewport();
      });
      const hideHandle = await Keyboard.addListener("keyboardWillHide", keyboardClosed);
      if (disposed) {
        showHandle.remove();
        hideHandle.remove();
        return;
      }
      nativeHandles = [showHandle, hideHandle];
    };

    updateViewport();
    viewport?.addEventListener("resize", updateViewport);
    viewport?.addEventListener("scroll", updateViewport);
    window.addEventListener("resize", updateViewport);
    installNativeListeners().catch(() => {});
    return () => {
      disposed = true;
      nativeHandles.forEach((handle) => handle.remove());
      viewport?.removeEventListener("resize", updateViewport);
      viewport?.removeEventListener("scroll", updateViewport);
      window.removeEventListener("resize", updateViewport);
    };
  }, [sifreSifirlamaModu, sifirlamaAlaniniGorunurTut]);

  useEffect(() => {
    if (!kayitModu || !kayitAlanHatasi) return undefined;
    const refs = {
      name: nameFieldRef,
      surname: surnameFieldRef,
      birthDate: birthDateFieldRef,
      username: usernameFieldRef,
      email: emailFieldRef,
      password: passwordFieldRef,
      phone: phoneFieldRef,
    };
    const timer = window.setTimeout(() => {
      const container = registerScrollRef.current;
      const target = refs[hataAlani(kayitAlanHatasi)]?.current;
      if (!container || !target) return;
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const desiredTop = container.scrollTop + (targetRect.top - containerRect.top) - 22;
      container.scrollTo({ top: Math.max(0, desiredTop), behavior: "smooth" });
      if (hataAlani(kayitAlanHatasi) !== "email" || !bekleyenSosyalKayit) {
        window.setTimeout(() => target.querySelector("input")?.focus({ preventScroll: true }), 280);
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [bekleyenSosyalKayit, kayitAlanHatasi, kayitModu]);

  const renderSocialButtons = (compact = false) => (
    <div className={`authSocialButtons ${compact ? "authSocialButtonsCompact" : ""}`}>
      <button
        type="button"
        className="authSocialButton authSocialGoogle"
        onClick={() => sosyalGirisYap?.("google.com")}
        disabled={yukleniyor}
      >
        <span className="authSocialGoogleMark" aria-hidden="true">
          <svg viewBox="0 0 18 18" focusable="false">
            <path fill="#4285F4" d="M17.6 9.2c0-.6-.1-1.2-.2-1.8H9v3.4h4.8a4.1 4.1 0 0 1-1.8 2.7v2.2h2.9c1.7-1.6 2.7-3.8 2.7-6.5Z" />
            <path fill="#34A853" d="M9 18c2.4 0 4.5-.8 6-2.2L12 13.5c-.8.6-1.9.9-3 .9-2.3 0-4.3-1.6-5-3.7H1v2.3A9 9 0 0 0 9 18Z" />
            <path fill="#FBBC05" d="M4 10.7A5.4 5.4 0 0 1 4 7.3V5H1a9 9 0 0 0 0 8l3-2.3Z" />
            <path fill="#EA4335" d="M9 3.6c1.3 0 2.5.5 3.5 1.4L15 2.5A8.5 8.5 0 0 0 9 0a9 9 0 0 0-8 5l3 2.3c.7-2.1 2.7-3.7 5-3.7Z" />
          </svg>
        </span>
        <span>{t("auth.social.google")}</span>
      </button>
      <button
        type="button"
        className="authSocialButton authSocialApple"
        onClick={() => sosyalGirisYap?.("apple.com")}
        disabled={yukleniyor}
      >
        {/* MADDE 16: Onceki surumde Apple logosu U+F8FF ozel karakteriyle
            gosteriliyordu; bu glyph yalnizca Apple cihazlarinin sistem fontunda
            bulunur. Android'de karsiligi olmadigi icin bozuk kutu/soru isareti
            gorunuyordu. Google butonu gibi burada da gercek bir SVG logo
            kullaniyoruz; tum platformlarda (iOS/Android/web) dogru gorunur.
            currentColor ile buton metniyle ayni renkte kalir (acik/koyu tema uyumlu). */}
        <span className="authSocialAppleMark" aria-hidden="true">
          <svg viewBox="0 0 384 512" width="16" height="16" focusable="false">
            <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
          </svg>
        </span>
        <span>{t("auth.social.apple")}</span>
      </button>
    </div>
  );

  const temaAcik = tema === "light";
  const temaDegistir = () => {
    if (typeof setTema !== "function") return;
    const yeniTema = temaAcik ? "dark" : "light";
    setTema(yeniTema);
    document.body.setAttribute("data-tema", yeniTema);
    document.documentElement.setAttribute("data-tema", yeniTema);
    localStorage.setItem("tema", yeniTema);
  };

  const renderThemeToggle = () => (
    <button
      type="button"
      className={temaAcik ? "authThemeToggleBtn light" : "authThemeToggleBtn dark"}
      onClick={temaDegistir}
      aria-label={t("settings.theme")}
      title={temaAcik ? t("settings.theme.dark") : t("settings.theme.light")}
    >
      <AuthGlyph name={temaAcik ? "sun" : "moon"} />
    </button>
  );

  const handleKayitSubmit = useCallback(() => {
    if (yukleniyor) return;
    if (!yasalOnay) {
      setYasalOnayHatasi(true);
      window.setTimeout(() => {
        const container = registerScrollRef.current;
        const target = yasalOnayRef.current;
        if (!container || !target) return;
        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const desiredTop = container.scrollTop + (targetRect.top - containerRect.top) - 18;
        container.scrollTo({ top: Math.max(0, desiredTop), behavior: "smooth" });
      }, 0);
      return;
    }
    setYasalOnayHatasi(false);
    kayitOl?.();
  }, [kayitOl, yasalOnay, yukleniyor]);

  const renderHero = () => (
    <div className="authHero">
      <div className="authHeroLogo">
        <img src="/logo512.png" alt="Aile Yönetimi" className="authHeroLogoImage" />
      </div>
      <div className="authHeroTypewriter">
        <TypewriterText text={t("app.name")} speed={110} />
      </div>
      <p className="authHeroSub">{t("app.tagline")}</p>
      <AileSahnesi />
    </div>
  );

  return (
    <div
      ref={authPageRef}
      className={`authPage2 ${!emailDogrulamaBekliyor && !kayitModu ? "authLoginPage" : ""} ${kayitModu ? "authRegisterPage" : ""} ${sifreSifirlamaModu ? "authForgotMode" : ""} ${verificationKeyboardHeight > 0 ? "authVerifyKeyboardOpen" : ""}`}
      style={{
        ...(sifreSifirlamaModu ? { "--auth-visual-height": authViewportHeight + "px" } : {}),
        ...(verificationKeyboardHeight > 0 ? { "--auth-verify-kb-inset": verificationKeyboardHeight + "px" } : {}),
      }}
    >
      {/* Tema seçici — sol üst / Dil seçici — sağ üst */}
      {renderThemeToggle()}
      <LanguageSwitcher variant="auth" />

      {/* Zengin arka plan */}
      <div className="authParticles">
        {[...Array(25)].map((_, i) => (
          <div
            key={i}
            className={`authParticle authParticle${i % 4}`}
            style={{
              left: `${Math.floor((i * 13 + 7) % 94)}%`,
              width: `${2 + (i % 3)}px`,
              height: `${2 + (i % 3)}px`,
              animationDelay: `${(i * 0.35) % 7}s`,
              animationDuration: `${6 + (i % 6)}s`,
            }}
          />
        ))}
        {["👨‍👩‍👧‍👦", "🏡", "❤️", "⭐", "🌟", "👶", "👩", "👨"].map((e, i) => (
          <div
            key={"e" + i}
            className="authFloatEmoji"
            style={{
              left: `${(i * 12 + 5) % 88}%`,
              animationDelay: `${i * 0.9}s`,
              animationDuration: `${8 + i * 0.7}s`,
              fontSize: `${16 + (i % 3) * 6}px`,
            }}
          >{e}</div>
        ))}
      </div>
      <div className="authGrid" />
      <div className="authGlow authGlow1" />
      <div className="authGlow authGlow2" />
      <div className="authGlow authGlow3" />

      {emailDogrulamaBekliyor ? (
        /* ── E-POSTA DOĞRULAMA EKRANI ── */
        <div className="authScreen authVerifyScreen">
          {renderHero()}
          <div className="authFormCard authVerifyCard">
            <div className="authFormCardGlow" />
            <div className="authVerifyIcon">📧</div>
            <h2 className="authFormTitle">{t("auth.verifyEmail")}</h2>
            <p className="authFormSub">{t("auth.verifyEmailDesc")}</p>

            <div className="authInfoBox2">
              <strong>{userEmail || t("auth.placeholder.email")}</strong>
              <span>{t("auth.verifyEmailInfo1")}</span>
            </div>

            <ol className="authVerifySteps">
              <li>{t("auth.verifyStepInbox")}</li>
              <li>{t("auth.verifyStepSpam")}</li>
              <li>{t("auth.verifyStepReturn")}</li>
            </ol>

            {bilgiMesaji && <div className="authSuccessBox2">✅ {bilgiMesaji}</div>}
            {hata && <div className="authErrorBox2">⚠️ {hata}</div>}

            <button onClick={emailDogrulamaKontrolEt} disabled={yukleniyor} className="authLoginBtn">
              {yukleniyor ? (
                <span className="authBtnLoading">
                  <span className="authDot" /><span className="authDot" /><span className="authDot" />
                </span>
              ) : (
                <span>{t("auth.verifyCheck")} <span className="authBtnArrow">→</span></span>
              )}
            </button>

            <button
              type="button"
              onClick={resendVerification}
              disabled={yukleniyor || verificationCooldown > 0}
              className="authSecondaryActionBtn"
            >
              {verificationCooldown > 0
                ? t("auth.resendVerificationCountdown", { seconds: verificationCooldown })
                : t("auth.resendVerification")}
            </button>

            {!verificationEmailEditOpen ? (
              <button
                type="button"
                onClick={() => {
                  setVerificationNextEmail(userEmail || "");
                  setVerificationPassword("");
                  setVerificationEmailEditOpen(true);
                }}
                className="authTextBtn authVerifyEditToggle"
              >
                {t("auth.wrongVerificationEmail")}
              </button>
            ) : (
              <form className="authVerifyEmailEdit" onSubmit={updateVerificationEmail}>
                <label>
                  <span>{t("auth.newVerificationEmail")}</span>
                  <input
                    type="email"
                    value={verificationNextEmail}
                    onChange={(event) => setVerificationNextEmail(event.target.value)}
                    autoComplete="email"
                    required
                  />
                </label>
                <label>
                  <span>{t("auth.currentPassword")}</span>
                  <input
                    ref={verificationPasswordRef}
                    type="password"
                    value={verificationPassword}
                    onChange={(event) => setVerificationPassword(event.target.value)}
                    onFocus={() => verificationPasswordAlaniniGorunurTut(verificationKeyboardHeightRef.current)}
                    autoComplete="current-password"
                    required
                  />
                </label>
                <div className="authVerifyEmailEditActions">
                  <button type="submit" className="authSecondaryActionBtn" disabled={yukleniyor}>{t("auth.updateEmailAndSend")}</button>
                  <button type="button" className="authTextBtn" disabled={yukleniyor} onClick={() => {
                    setVerificationEmailEditOpen(false);
                    setVerificationNextEmail("");
                    setVerificationPassword("");
                  }}>{t("common.cancel")}</button>
                </div>
              </form>
            )}

            <button type="button" onClick={cikisYap} className="authTextBtn">
              {t("auth.diffAccount")}
            </button>
          </div>
        </div>
      ) : !kayitModu ? (
        /* ── GİRİŞ EKRANI ── */
        <div className="authScreen authLoginScreen">
          {renderHero()}

          <div className="authFormCard">
            <div className="authFormCardGlow" />
            <h2 className="authFormTitle">{t("auth.welcome")}</h2>
            <p className="authFormSub">
              {sifreSifirlamaModu ? t("auth.resetSub") : t("auth.loginSub")}
            </p>

            <label className="authFieldLabel2" htmlFor="auth-login-info">
              {t("auth.placeholder.loginInfo")}
            </label>
            <div className="authInputGroup2">
              <div className="authInputIcon2"><AuthGlyph name="user" /></div>
              <input
                id="auth-login-info"
                ref={loginInputRef}
                className="authInputNew2"
                type="text"
                placeholder={t("auth.placeholder.loginInfo")}
                value={loginBilgi}
                onChange={(e) => setLoginBilgi(e.target.value)}
                autoComplete="username"
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck="false"
                onFocus={sifirlamaAlaniniGorunurTut}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (sifreSifirlamaModu) sifremiUnuttum?.();
                    else sifreInputRef.current?.focus();
                  }
                }}
              />
            </div>

            {!sifreSifirlamaModu && (
              <>
                <label className="authFieldLabel2" htmlFor="auth-login-password">
                  {t("auth.placeholder.password")}
                </label>
                <div className="authInputGroup2 authPasswordGroup">
                  <div className="authInputIcon2"><AuthGlyph name="lock" /></div>
                  <input
                    id="auth-login-password"
                    className="authInputNew2"
                    type={sifreGoster ? "text" : "password"}
                    placeholder={t("auth.placeholder.password")}
                    value={sifre}
                    ref={sifreInputRef}
                    onChange={(e) => setSifre(e.target.value)}
                    autoComplete="current-password"
                    autoCorrect="off"
                    autoCapitalize="none"
                    spellCheck="false"
                    onKeyDown={(e) => e.key === "Enter" && girisYap()}
                  />
                  <button
                    type="button"
                    className="authPasswordToggle"
                    onClick={() => setSifreGoster(!sifreGoster)}
                    aria-label={sifreGoster ? t("auth.hidePassword") : t("auth.showPassword")}
                  >
                    <AuthGlyph name={sifreGoster ? "eyeOff" : "eye"} />
                  </button>
                </div>
              </>
            )}

            {!sifreSifirlamaModu && (
              <div className="authForgotRow">
                <button
                  type="button"
                  className="authForgotBtn"
                  onClick={() => { setSifreSifirlamaModu(true); mesajlariTemizle(); }}
                >
                  {t("auth.forgotPassword")}
                </button>
              </div>
            )}

            {bilgiMesaji && <div className="authSuccessBox2">✅ {bilgiMesaji}</div>}
            {hata && <div className="authErrorBox2">⚠️ {hata}</div>}

            <button
              onClick={sifreSifirlamaModu ? sifremiUnuttum : girisYap}
              disabled={yukleniyor}
              className="authLoginBtn"
            >
              {yukleniyor ? (
                <span className="authBtnLoading">
                  <span className="authDot" /><span className="authDot" /><span className="authDot" />
                </span>
              ) : sifreSifirlamaModu ? (
                <span>{t("auth.sendResetMail")} <span className="authBtnArrow">→</span></span>
              ) : (
                <span>{t("auth.login")} <span className="authBtnArrow">→</span></span>
              )}
            </button>

            {sifreSifirlamaModu ? (
              <button
                type="button"
                onClick={() => { setSifreSifirlamaModu(false); mesajlariTemizle(); }}
                className="authSecondaryActionBtn"
              >
                {t("auth.backToLogin")}
              </button>
            ) : (
              <>
                <div className="authDivider2"><span>{t("common.or")}</span></div>
                {renderSocialButtons()}
                <button onClick={() => { setKayitModu(true); mesajlariTemizle(); }} className="authRegisterBtn">
                  <span>{t("auth.register")}</span>
                </button>
              </>
            )}

            <nav className="authLegalLinks" aria-label={t("auth.legalLinksLabel")}>
              <button type="button" className="authLegalTextLink" onClick={() => openAuthLegal("privacy")}>{t("auth.privacyPolicy")}</button>
              <span aria-hidden="true">•</span>
              <button type="button" className="authLegalTextLink" onClick={() => openAuthLegal("terms")}>{t("auth.termsOfService")}</button>
            </nav>

            <p className="authFooterNote">{t("auth.footer")}</p>
          </div>
        </div>
      ) : (
        /* ── KAYIT EKRANI ── */
        <div className="authScreen authRegisterScreen">
          <div className="authFormCard authRegisterCard authRegisterCardV2">
            <div className="authFormCardGlow authRegisterGlow" />
            <header className="authRegisterHeaderV2">
              <button
                onClick={() => {
                  if (bekleyenSosyalKayit) sosyalAkisiIptalEt?.();
                  else setKayitModu(false);
                  mesajlariTemizle();
                }}
                className="authBackBtnV2"
                aria-label={t("common.back")}
              >←</button>
              <div className="authRegisterHeadingV2">
                <h2 className="authFormTitle authRegisterTitleV2">
                  {bekleyenSosyalKayit ? t("auth.social.completeProfile") : t("auth.createAccount")}
                </h2>
                <p className="authFormSub authRegisterSubV2">
                  {bekleyenSosyalKayit ? t("auth.social.completeProfileSub") : t("auth.createAccountSub")}
                </p>
              </div>
            </header>
            <div ref={registerScrollRef} className="authRegisterScrollV2">
              {!bekleyenSosyalKayit && (
                <section className="authRegisterSectionV2 authRegisterSocialSection">
                  <div className="authSectionHeadingV2"><span>{t("auth.social.quickRegister")}</span></div>
                  {renderSocialButtons(true)}
                  <div className="authDivider2 authRegisterSocialDivider"><span>{t("auth.social.orForm")}</span></div>
                </section>
              )}
              {bekleyenSosyalKayit && (
                <div className="authSocialConnectedBadge">
                  {/* MADDE 16: Apple U+F8FF ozel karakteri Android'de bozuk gorunuyordu.
                      Onay rozetinde Apple icin SVG logo, Google icin "G" harfi kullaniyoruz. */}
                  <span aria-hidden="true">
                    {bekleyenSosyalKayit.provider === "apple.com" ? (
                      <svg viewBox="0 0 384 512" width="15" height="15" focusable="false">
                        <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                      </svg>
                    ) : "G"}
                  </span>
                  <strong>
                    {bekleyenSosyalKayit.provider === "apple.com"
                      ? t("auth.social.appleConnected")
                      : t("auth.social.googleConnected")}
                  </strong>
                </div>
              )}
              <section className="authRegisterSectionV2">
                <div className="authSectionHeadingV2"><span>{t("auth.register.personalInfo")}</span><small>{t("auth.register.required")}</small></div>
                <div className="authFormGrid authFormGridV2">
                  <label ref={nameFieldRef} className={`authFieldV2 ${alandaHataVar("name") ? "hasError" : ""}`}><span className="authFieldLabelV2">{t("auth.register.name")}</span><span className="authFieldControlV2"><span className="authFieldIconV2">👤</span><input className="authFieldInputV2" placeholder={t("auth.placeholder.name")} value={ad} onChange={(e) => { setAd(e.target.value); alaniTemizle("name"); }} autoComplete="given-name" aria-invalid={alandaHataVar("name")} /></span>{alandaHataVar("name") && <small className="authFieldErrorV2" role="alert">{kayitAlanHataMetni(kayitAlanHatasi)}</small>}</label>
                  <label ref={surnameFieldRef} className={`authFieldV2 ${alandaHataVar("surname") ? "hasError" : ""}`}><span className="authFieldLabelV2">{t("auth.register.surname")}</span><span className="authFieldControlV2"><span className="authFieldIconV2">👤</span><input className="authFieldInputV2" placeholder={t("auth.placeholder.surname")} value={soyad} onChange={(e) => { setSoyad(e.target.value); alaniTemizle("surname"); }} autoComplete="family-name" aria-invalid={alandaHataVar("surname")} /></span>{alandaHataVar("surname") && <small className="authFieldErrorV2" role="alert">{kayitAlanHataMetni(kayitAlanHatasi)}</small>}</label>
                </div>
                <label ref={birthDateFieldRef} className={`authFieldV2 authBirthFieldV2 ${alandaHataVar("birthDate") ? "hasError" : ""}`}><span className="authFieldLabelV2">{t("auth.register.birthDate")}</span><span className="authFieldControlV2"><span className="authFieldIconV2">🎂</span><input className="authFieldInputV2 authDateInputV2" type="date" value={dogumTarihi} onChange={(e) => { setDogumTarihi(e.target.value); alaniTemizle("birthDate"); }} aria-invalid={alandaHataVar("birthDate")} /></span>{alandaHataVar("birthDate") ? <small className="authFieldErrorV2" role="alert">{kayitAlanHataMetni(kayitAlanHatasi)}</small> : <small className="authFieldHelpV2">{t("auth.register.birthDateHelp")}</small>}</label>
                <label className="authFieldV2"><span className="authFieldLabelV2">{t("auth.register.gender")}</span><span className="authFieldControlV2"><span className="authFieldIconV2">⚧</span><select className="authFieldInputV2" value={cinsiyet} onChange={(e) => setCinsiyet(e.target.value)}>{cinsiyetler.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}</select></span></label>
              </section>
              <section className="authRegisterSectionV2">
                <div className="authSectionHeadingV2"><span>{t("auth.register.familyProfile")}</span></div>
                <label ref={usernameFieldRef} className={`authFieldV2 ${alandaHataVar("username") ? "hasError" : ""}`}><span className="authFieldLabelV2">{t("auth.register.username")}</span><span className="authFieldControlV2"><span className="authFieldIconV2">@</span><input className="authFieldInputV2" placeholder={t("auth.placeholder.username")} value={kullaniciAdi} onChange={(e) => { setKullaniciAdi(e.target.value); alaniTemizle("username"); }} autoComplete="username" autoCorrect="off" autoCapitalize="none" spellCheck="false" aria-invalid={alandaHataVar("username")} /></span>{alandaHataVar("username") && <small className="authFieldErrorV2" role="alert">{kayitAlanHataMetni(kayitAlanHatasi)}</small>}</label>
                <label className="authFieldV2"><span className="authFieldLabelV2">{t("auth.register.role")}</span><span className="authFieldControlV2"><span className="authFieldIconV2">👥</span><select className="authFieldInputV2" value={rol} onChange={(e) => setRol(e.target.value)}>{roller.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}</select></span></label>
              </section>
              <section className="authRegisterSectionV2">
                <div className="authSectionHeadingV2"><span>{t("auth.register.accountInfo")}</span></div>
                <label ref={emailFieldRef} className={`authFieldV2 ${alandaHataVar("email") ? "hasError" : ""}`}><span className="authFieldLabelV2">{t("auth.register.email")}</span><span className="authFieldControlV2"><span className="authFieldIconV2">✉</span><input className="authFieldInputV2" type="email" placeholder={t("auth.placeholder.email")} value={email} onChange={(e) => { setEmail(e.target.value); alaniTemizle("email"); }} autoComplete="email" readOnly={Boolean(bekleyenSosyalKayit)} aria-invalid={alandaHataVar("email")} /></span>{alandaHataVar("email") && <small className="authFieldErrorV2" role="alert">{kayitAlanHataMetni(kayitAlanHatasi)}</small>}</label>
                {!bekleyenSosyalKayit && <label ref={passwordFieldRef} className={`authFieldV2 ${alandaHataVar("password") ? "hasError" : ""}`}><span className="authFieldLabelV2">{t("auth.register.password")}</span><span className="authFieldControlV2"><span className="authFieldIconV2">🔒</span><input className="authFieldInputV2" type="password" minLength={8} maxLength={128} placeholder={t("auth.placeholder.passwordMin")} value={kayitSifre} onChange={(e) => { setKayitSifre(e.target.value); alaniTemizle("password"); }} autoComplete="new-password" aria-invalid={alandaHataVar("password")} /></span>{alandaHataVar("password") && <small className="authFieldErrorV2" role="alert">{kayitAlanHataMetni(kayitAlanHatasi)}</small>}</label>}
              </section>
              <section className="authRegisterSectionV2">
                <div className="authSectionHeadingV2"><span>{t("auth.register.phone")}</span><small>{t("auth.register.optional")}</small></div>
                <label ref={phoneFieldRef} className={"authFieldV2 " + (kayitAlanHatasi === "phone" ? "hasError" : "")}><span className="authFieldLabelV2">{t("auth.register.phone")}</span><span className="authFieldControlV2"><span className="authFieldIconV2">📱</span><span className="authPhonePrefixV2" aria-hidden="true">+</span><input className="authFieldInputV2 authPhoneInputV2" inputMode="tel" value={telefon} onChange={(e) => { setTelefon(e.target.value.replace(/[^\d\s]/g, "")); if (kayitAlanHatasi === "phone") setKayitAlanHatasi?.(""); }} autoComplete="tel" autoCorrect="off" autoCapitalize="none" spellCheck="false" aria-label={t("auth.register.phone")} aria-invalid={kayitAlanHatasi === "phone"} /></span>{kayitAlanHatasi === "phone" && <small className="authFieldErrorV2" role="alert">⚠️ {kayitAlanHataMetni("phone")}</small>}</label>
              </section>
              {!areAndroidHealthFeaturesDisabled() && <section className={`authOptionalSectionV2 ${saglikBilgileriAcik ? "isOpen" : ""}`}>
                <button type="button" className="authOptionalToggleV2" onClick={() => setSaglikBilgileriAcik((v) => !v)} aria-expanded={saglikBilgileriAcik}><span className="authOptionalIconV2">♡</span><span><strong>{t("auth.register.healthInfo")}</strong><small>{t("auth.register.optional")}</small></span><span className="authOptionalChevronV2">⌄</span></button>
                {saglikBilgileriAcik && <div className="authOptionalBodyV2"><label className="authFieldV2"><span className="authFieldLabelV2">{t("auth.register.bloodGroup")}</span><span className="authFieldControlV2"><span className="authFieldIconV2">🩸</span><select className="authFieldInputV2" value={kanGrubu} onChange={(e) => setKanGrubu(e.target.value)}><option value="">{t("auth.placeholder.bloodGroup")}</option>{["A Rh+", "A Rh-", "B Rh+", "B Rh-", "AB Rh+", "AB Rh-", "0 Rh+", "0 Rh-"].map((k) => <option key={k}>{k}</option>)}</select></span></label><div className="authFormGrid authFormGridV2"><label className="authFieldV2"><span className="authFieldLabelV2">{t("auth.register.height")}</span><span className="authFieldControlV2"><span className="authFieldIconV2">↕</span><input className="authFieldInputV2" inputMode="decimal" placeholder={t("auth.placeholder.heightCm")} value={boy} onChange={(e) => setBoy(e.target.value)} /></span></label><label className="authFieldV2"><span className="authFieldLabelV2">{t("auth.register.weight")}</span><span className="authFieldControlV2"><span className="authFieldIconV2">⚖</span><input className="authFieldInputV2" inputMode="decimal" placeholder={t("auth.placeholder.weightKg")} value={kilo} onChange={(e) => setKilo(e.target.value)} /></span></label></div></div>}
              </section>}
              <div ref={yasalOnayRef} className={`authLegalConsentWrapV2 ${yasalOnayHatasi ? "hasError" : ""}`}><label className={`authLegalConsent authLegalConsentV2 ${yasalOnay ? "isChecked" : ""} ${yasalOnayHatasi ? "hasError" : ""}`}><input type="checkbox" checked={Boolean(yasalOnay)} onChange={(event) => { const checked = event.target.checked; setYasalOnay?.(checked); if (checked) setYasalOnayHatasi(false); }} /><span className="authLegalCheckbox" aria-hidden="true">{yasalOnay ? "✓" : ""}</span><span className="authLegalConsentText">{t("auth.legalConsentPrefix")} <button type="button" className="authLegalTextLink" onClick={(event) => { event.preventDefault(); event.stopPropagation(); openAuthLegal("terms"); }}>{t("auth.termsOfService")}</button> {t("auth.legalConsentAnd")} <button type="button" className="authLegalTextLink" onClick={(event) => { event.preventDefault(); event.stopPropagation(); openAuthLegal("privacy"); }}>{t("auth.privacyPolicy")}</button> {t("auth.legalConsentSuffix")}</span></label>{yasalOnayHatasi && <div className="authLegalConsentErrorV2" role="alert">⚠️ {t("auth.error.legalConsentRequired")}</div>}</div>
              {bilgiMesaji && <div className="authSuccessBox2">✅ {bilgiMesaji}</div>}{hata && <div className="authErrorBox2">⚠️ {hata}</div>}
            </div>
            <footer className="authRegisterFooterV2"><button onClick={handleKayitSubmit} disabled={yukleniyor} className="authLoginBtn authRegisterSubmitV2">{yukleniyor ? <span className="authBtnLoading"><span className="authDot" /><span className="authDot" /><span className="authDot" /></span> : <span>{t("auth.createAccount")} <span className="authBtnArrow">→</span></span>}</button></footer>
          </div>
        </div>
      )}

      {authLegalDocument && createPortal(
        <div ref={legalLayerRef} className="authLegalPageLayer authLegalLayerOpening">
          <LegalDocumentPage
            documentKey={authLegalDocument}
            embedded
            onBack={closeAuthLegal}
            onNavigate={(documentKey) => {
              if (!documentKey || documentKey === authLegalDocument) return;
              setAuthLegalDocument(documentKey);
              requestAnimationFrame(() => {
                const layer = legalLayerRef.current;
                if (!layer) return;
                layer.classList.remove("authLegalLayerOpening");
                void layer.offsetWidth;
                layer.classList.add("authLegalLayerOpening");
              });
            }}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
